import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip, Modal, Button, Slider, IconButton } from '@mui/material';
import { InfoOutlinedIcon, SlideshowOutlinedIcon, GifBoxOutlinedIcon, InsertPhotoOutlinedIcon, CloseIcon, LockOutlinedIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils'; // Helper function to get the cropped image
import { useCardContext } from 'contexts/CardContext'; // Import the CardContext
import { useParams, useNavigate } from 'react-router-dom';
import { getCardById } from 'apis/cardApi'; // Adjust the import path as needed
import { validateUUID } from 'utils/validator';
import { useTeam } from 'team/contexts/teamContext';
import { useTeamAssets } from 'team/hooks/useTeamAssets';
import { TEAM_ASSET_TYPES } from 'team/apis/teamAssetApi';
import TeamAssetPickerPopover from './TeamAssetPickerPopover';


const TeamCoverPhoto = ({
    onCroppedImage,
    onDeleteCoverPhoto,
    initialImageUrl,
    isRestricted = false,
    canRequestChange = false,
    onRestrictionNotification
}) => {
    const [image, setImage] = useState(null);
    const [open, setOpen] = useState(false);
    const [assetAnchorEl, setAssetAnchorEl] = useState(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isSelectingAsset, setIsSelectingAsset] = useState(false);

    // Initial zoom state should be 1 for original size
    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [croppedImage, setCroppedImage] = useState(null); // Store the cropped image URL
    const [objectUrl, setObjectUrl] = useState(null);
    const { cardState, setCardCoverPhotoUrl } = useCardContext();
    const { cardId } = useParams(); // Extracts the `cardId` from the URL
    const [cardDetails, setCardDetails] = useState(null);
    const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
    const cardCoverPhotoUrl = cardState[cardId]?.cardCoverPhotoUrl;
    const fileInputRef = useRef(null);
    const { currentTeamId } = useTeam();
    const { assets: coverAssets = [] } = useTeamAssets(currentTeamId, TEAM_ASSET_TYPES.COVER_PHOTO);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true); // State to handle loading state
    const [inputDisabled, setInputDisabled] = useState(false); // State to disable input

    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        return 1; // Always return 1 to show image at its original size
    }, []);




    // Modify onMediaLoaded to be simpler
    const onMediaLoaded = useCallback(() => {
        setZoom(1); // Always start at original size
        setCrop({ x: 0, y: 0 });
    }, []);

    // Update handleImageChange
    const handleImageChange = (event) => {
        // Check if field is restricted
        if (isRestricted) {
            const message = 'This section comes from a template and is unable to be edited';
            if (onRestrictionNotification) {
                onRestrictionNotification(message);
            }
            event.target.value = '';
            return;
        }

        if (event.target.files && event.target.files[0]) {
            const file = URL.createObjectURL(event.target.files[0]);
            setObjectUrl(file);
            setImage(file);
            setOpen(true);
            setAssetAnchorEl(null);
            event.target.value = '';
        }
    };


    useEffect(() => {
        if (cardDetails?.card && !validateUUID(cardId)) { // Ensure cardDetails.card exists

            setCoverPhotoUrl(cardDetails.card.coverPhotoUrl || '');

        }
    }, [cardDetails]);



    useEffect(() => {
        if (!validateUUID(cardId)) {
            console.log('Invalid UUID, resetting state');
            // Clear local state
            setCardCoverPhotoUrl(cardId, null);

            // Ensure the cleanup function is called on unmount
            return () => {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            };
        }
    }, [cardId, objectUrl]); // Include cardId and objectUrl in the dependency array



    useEffect(() => {
        const fetchCardDetails = async () => {
            try {
                const data = await getCardById(cardId);
                setCardDetails(data);
            } catch (error) {
                console.error('Error fetching card details:', error);
            }
        };

        if (cardId && !validateUUID(cardId)) {
            fetchCardDetails();
        }
    }, [cardId]);




    useEffect(() => {
        if (cardDetails && !validateUUID(cardId)) {
            setCroppedImage(cardDetails.card.coverPhotoUrl);
        }
    }, [cardDetails]);

    // For templates: use initialImageUrl if provided and no valid cardId
    useEffect(() => {
        if (initialImageUrl && (!cardId || validateUUID(cardId))) {
            setCroppedImage(initialImageUrl);
        }
    }, [initialImageUrl, cardId]);

    // ✅ SYNC FROM CONTEXT: Update croppedImage when context changes (e.g., after template is applied)
    // Only update if user is not actively editing (modal is closed and no image is being cropped)
    useEffect(() => {
        if (!cardId || validateUUID(cardId)) return; // Skip for new cards

        // Only sync from context if user is not actively editing
        if (!open && !image && cardCoverPhotoUrl !== undefined) {
            // Normalize: treat empty string as null
            const contextCoverUrl = (cardCoverPhotoUrl && cardCoverPhotoUrl.trim()) ? cardCoverPhotoUrl : null;

            // Only update if different to prevent unnecessary re-renders
            setCroppedImage(prev => {
                // Normalize prev value for comparison
                const normalizedPrev = (prev && prev.trim()) ? prev : null;

                // If context value is different from current, update
                if (normalizedPrev !== contextCoverUrl) {
                    return contextCoverUrl;
                }
                return prev;
            });
        }
    }, [cardId, cardCoverPhotoUrl, open, image]); // Watch context changes





    // Function to remove the image from Firebase Storage
    const handleRemoveImage = async () => {
        // Check if field is restricted
        if (isRestricted) {
            const message = 'This section comes from a template and is unable to be edited';
            if (onRestrictionNotification) {
                onRestrictionNotification(message);
            }
            return;
        }

        // Call the deletion method passed from the parent component
        if (onDeleteCoverPhoto) {
            await onDeleteCoverPhoto();
        }

        setCroppedImage(null); // Clear the cropped image
        setCardCoverPhotoUrl(cardId, null); // Update context with no image
        setImage(null); // Clear the file from the state

        if (objectUrl) {
            URL.revokeObjectURL(objectUrl); // Revoke the object URL
            setObjectUrl(null);
        }

        setInputDisabled(true); // Disable input to prevent click events
        setTimeout(() => setInputDisabled(false), 0); // Re-enable input after a short delay
    };



    useEffect(() => {
        console.log("TeamCoverPhotoUrl", cardCoverPhotoUrl);
        console.log("cropped image", croppedImage);
        console.log("Image", image);
    }, [cardCoverPhotoUrl, croppedImage, image]);


    // Update handleClose
    const handleClose = () => {
        setOpen(false);
        setImage(null);
        setObjectUrl(null);
        setZoom(1);
    };
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);







    const handleCoverPhotoClick = (event) => {
        if (isRestricted) {
            const message = 'This section comes from a template and is unable to be edited';
            if (onRestrictionNotification) {
                onRestrictionNotification(message);
            }
            return;
        }
        setAssetAnchorEl(event.currentTarget);
    };

    const handleUploadOwnCoverPhoto = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleSelectTeamAsset = (asset) => {
        if (isRestricted || isSelectingAsset) return;
        if (!asset?.url) return;
        
        // Update local state and context for immediate preview
        setCroppedImage(asset.url);
        setCardCoverPhotoUrl(cardId, asset.url);
        
        // Notify parent. We pass the URL string; the parent should handle it.
        if (onCroppedImage) {
            onCroppedImage(asset.url);
        }
        
        setAssetAnchorEl(null);
    };

    const handleSave = async () => {
        try {
            const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, 'rectangle', `cover-${cardId}`); // Pass image type


            if (onCroppedImage) {
                onCroppedImage(croppedImageBlob); // Pass the Blob to the parent component
            }



            // Generate a URL for the cropped image Blob
            const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
            setCroppedImage(croppedImageUrl); // Save the cropped image URL

            // Store the URL in the context


            setCardCoverPhotoUrl(cardId, croppedImageUrl);



            // Close the modal
            setOpen(false);
        } catch (e) {
            console.error(e);
        }
    };


    return (
        <div>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={isRestricted}
                style={{ display: 'none' }}
            />
            {/* Cover Photo */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '6px' }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#4f4f4f',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        Cover Photo
                        <Tooltip title="Ideal Dimensions: 780 px by 300 px (2.1:1)" arrow>
                            <InfoOutlinedIcon sx={{ fontSize: '1rem', marginLeft: 0.5 }} />
                        </Tooltip>
                    </Typography>
                    {isRestricted && (
                        <Tooltip title="This field is locked by your assigned template">
                            <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                        </Tooltip>
                    )}
                </Box>


                <Box
                    sx={{
                        position: 'relative',
                        width: 250,
                        height: 100,
                    }}
                >


                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                            backgroundColor: '#f2f2f2',
                            border: '1px solid #dbdbdb',
                            cursor: isRestricted ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            textAlign: 'center',
                            padding: 1,
                            backgroundImage: croppedImage ? `url(${croppedImage})` : 'none', // Set cropped image as background
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: isRestricted ? 0.6 : 1,
                        }}
                        component="div"
                        onClick={handleCoverPhotoClick}
                    >
                        {!croppedImage && (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, marginBottom: 1 }}>
                                    <InsertPhotoOutlinedIcon sx={{ fontSize: '25px', color: '#828282' }} />
                                    <SlideshowOutlinedIcon sx={{ fontSize: '25px', color: '#828282' }} />
                                    <GifBoxOutlinedIcon sx={{ fontSize: '25px', color: '#828282' }} />
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}
                                >
                                    <span style={{ color: '#79bff7' }}>Select</span> image, video, gif file or drag and drop one here
                                </Typography>
                            </>
                        )}
                    </Box>


                    {/* Close Button - Only show if not restricted */}
                    {croppedImage && !isRestricted && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage();
                            }}
                            sx={{
                                position: 'absolute',
                                top: 1,
                                right: -14,
                                color: '#828282',
                                backgroundColor: 'rgb(255, 255, 255)',
                                '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                                width: 26,
                                height: 26,
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                zIndex: 2, // Ensure it is above the input and image
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            <TeamAssetPickerPopover
                anchorEl={assetAnchorEl}
                onClose={() => setAssetAnchorEl(null)}
                onUploadClick={handleUploadOwnCoverPhoto}
                assets={coverAssets}
                onSelectAsset={handleSelectTeamAsset}
                uploadButtonText="Upload your own cover photo"
                onEmptyActionClick={() => navigate('/team/team-assets')}
            />

            {/* Modal for cropping image */}
            <Modal
                open={open}
                onClose={handleClose}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backdropFilter: 'blur(7px)', // Apply blur effect to the background
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', // Add a semi-transparent white background for the overlay
                }}
            >
                <Box
                    sx={{
                        outline: 'none',
                        width: '90vw', // Make modal take up more width
                        maxWidth: '900px', // Limit max width for responsiveness
                        height: '90vh', // Take more height for a bigger editor
                        maxHeight: '700px', // Limit max height
                    }}
                >
                    {image && (
                        <>
                            {/* Container for Cropper */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 'calc(100% - 100px)', // Adjust the height to be large
                                    marginBottom: 2,
                                }}
                            >

                                <Cropper
                                    image={image}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={2.6}
                                    cropShape="rect"
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    onMediaLoaded={onMediaLoaded}
                                    objectFit="contain"
                                    centerImage={true}
                                    showGrid={false}
                                    minZoom={0.05}
                                    maxZoom={3}
                                    restrictPosition={false}
                                />

                            </Box>

                            {/* Controls for Zoom Slider and Buttons */}
                            <Box sx={{ textAlign: 'center' }}>

                                <Slider
                                    value={zoom}
                                    onChange={(e, newValue) => setZoom(newValue)}
                                    min={0.05}
                                    max={3}
                                    step={0.01}
                                    defaultValue={1.5}
                                    valueLabelDisplay="auto"
                                    sx={{ marginBottom: 2 }}
                                />
                                <Button onClick={handleClose} variant="contained" sx={{ marginRight: 2 }}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSave} variant="contained" color="primary">
                                    Crop
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
};

export default TeamCoverPhoto;